// SalesTableMonthly.js
import React, { useRef } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, CircularProgress, Typography ,Box} from "@mui/material";

const MONTHLY_COLUMNS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const FIXED_COLUMNS = ['DEPT NO', 'ACCOUNT', 'CUSTOMER', 'TYPE', 'YEAR'];
const TOTAL_COLUMNS = FIXED_COLUMNS.length + MONTHLY_COLUMNS.length + 1; // 5 + 12 + TOTAL = 18

// Assuming formatNumber helper is passed down or available
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SalesTableMonthly = ({ rows, grandColumnTotals, loading, loadNextPage, hasMore }) => {

    const tableContainerRef = useRef(null);
    const handleTableScroll = () => {

        console.log("scrolled-->")
        const currentTarget = tableContainerRef.current;

        if (!currentTarget || loading || !hasMore || rows.length === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        // Load the next page when reaching the bottom (with a 10px buffer)
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };


    const renderHeader = () => (
        <TableHead>
            <TableRow>
                {FIXED_COLUMNS.map(header => (
                    <TableCell key={header} style={{ minWidth: 120 }}>{header}</TableCell>
                ))}
                {MONTHLY_COLUMNS.map(month => (
                    <TableCell key={month} align="right" style={{ minWidth: 90 }}>{month}</TableCell>
                ))}
                <TableCell align="right" style={{ minWidth: 100 }}>TOTAL</TableCell>
            </TableRow>
        </TableHead>
    );

    const renderBody = () => (
        <TableBody>
            {rows.map((row, index) => (
                <TableRow key={`month-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>{row.DEPTNO}</TableCell>
                    <TableCell>{row.ACCOUNT}</TableCell>
                    <TableCell>{row.CUSTOMER}</TableCell>
                    <TableCell>{row.TYPE}</TableCell>
                    <TableCell>{row.YEAR}</TableCell>
                    {MONTHLY_COLUMNS.map(month => (
                        <TableCell key={month} align="right">{formatNumber(row[month])}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row.TOTAL)}</TableCell>
                </TableRow>
            ))}

            {/* Load More Indicator */}
            {hasMore && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="caption" display="block">
                        Loading more data...
                    </Typography>
                </Box>
            )}

            {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={TOTAL_COLUMNS} align="center"><Typography variant="subtitle1" color="textSecondary">No monthly data found.</Typography></TableCell></TableRow>
            )}
        </TableBody>
    );

    const renderFooter = () => {
        // Grand totals only apply to month columns and TOTAL
        const totalKeys = [...MONTHLY_COLUMNS, 'TOTAL'];
        const descriptiveSpan = TOTAL_COLUMNS - totalKeys.length;

        return (
            <TableFooter>
                <TableRow sx={{ backgroundColor: '#b3b3b3', borderTop: '3px double #000' }}>
                    <TableCell colSpan={descriptiveSpan} align="right"
                        sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}>
                        GRAND TOTAL:
                    </TableCell>
                    {totalKeys.map(key => (
                        <TableCell key={key} align="right"
                            sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}>
                            {formatNumber(grandColumnTotals[key])}
                        </TableCell>
                    ))}
                </TableRow>
            </TableFooter>
        );
    };

    return (
        <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll} // ⬅️ ATTACH SCROLL HANDLER HERE
            sx={{
                maxHeight: 'calc(100vh - 350px)',
                overflowY: 'auto', width: '100%'
            }}
        >
            <Table stickyHeader aria-label="monthly sales report">
                {renderHeader()}
                {renderBody()}
                {Object.keys(grandColumnTotals).length > 0 && renderFooter()}
            </Table>

            {loading && (
                <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                    <CircularProgress size={48} />
                </div>
            )}

        </TableContainer>
    );
};

export default SalesTableMonthly;